import React, { useState, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const DocumentCard = ({ file, concessionId, personId, onView, onDownload, onDelete, isTemp = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [thumbnailError, setThumbnailError] = useState(false);
  const isImage = ['.jpg', '.jpeg', '.png', '.gif'].includes(file.file_type?.toLowerCase());
  const isPdf = file.file_type?.toLowerCase() === '.pdf';
  const isGif = file.file_type?.toLowerCase() === '.gif';
  
  const getFileUrl = () => {
    if (isTemp) return file.preview || null;
    if (personId) {
      return `${API}/client/concessions/${concessionId}/persons/${personId}/files/${file.id}`;
    }
    return `${API}/client/concessions/${concessionId}/files/${file.id}`;
  };
  
  // Load thumbnail with authentication for non-temp images
  useEffect(() => {
    if (isImage && !isTemp && concessionId && file.id) {
      const loadThumbnail = async () => {
        try {
          const response = await fetch(getFileUrl(), {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          });
          if (response.ok) {
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            setThumbnailUrl(url);
          } else {
            setThumbnailError(true);
          }
        } catch (error) {
          setThumbnailError(true);
        }
      };
      loadThumbnail();
      
      return () => {
        if (thumbnailUrl) {
          URL.revokeObjectURL(thumbnailUrl);
        }
      };
    } else if (isTemp && file.preview) {
      setThumbnailUrl(file.preview);
    }
  }, [file.id, concessionId, personId, isImage, isTemp]);
  
  const getFileIcon = () => {
    if (isGif) return '🎞️';
    if (isImage) return '🖼️';
    if (isPdf) return '📄';
    return '📝';
  };
  
  const getFileTypeLabel = () => {
    if (isGif) return 'GIF';
    if (isImage) return 'Image';
    if (isPdf) return 'PDF';
    return 'Texte';
  };

  return (
    <div 
      style={{
        position: 'relative',
        backgroundColor: isHovered ? '#343A52' : '#2C3148',
        borderRadius: '10px',
        padding: '10px',
        border: isHovered ? '1px solid #00C2FF' : '1px solid #3A4359',
        transition: 'all 0.25s ease',
        transform: isHovered ? 'translateY(-3px)' : 'translateY(0)',
        boxShadow: isHovered ? '0 8px 20px rgba(0, 194, 255, 0.15)' : 'none'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Preview Thumbnail */}
      <div 
        style={{
          width: '100%',
          height: '110px',
          backgroundColor: '#1C2133',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '10px',
          overflow: 'hidden',
          cursor: 'pointer',
          position: 'relative'
        }}
        onClick={() => onView && onView(file)}
      >
        {isImage && thumbnailUrl && !thumbnailError ? (
          <img 
            src={thumbnailUrl}
            alt={file.original_filename}
            style={{width: '100%', height: '100%', objectFit: 'cover'}}
            onError={() => setThumbnailError(true)}
          />
        ) : null}
        <div style={{
          display: (isImage && thumbnailUrl && !thumbnailError) ? 'none' : 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%'
        }}>
          <span style={{fontSize: '36px', marginBottom: '4px'}}>{getFileIcon()}</span>
          <span style={{
            fontSize: '10px',
            color: '#5B638C',
            textTransform: 'uppercase',
            fontWeight: '600',
            letterSpacing: '0.5px'
          }}>{getFileTypeLabel()}</span>
        </div>
        
        {/* Overlay on hover */}
        {isHovered && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px'
          }}>
            <span style={{color: '#00C2FF', fontSize: '14px', fontWeight: '600'}}>
              👁️ Visualiser
            </span>
          </div>
        )}
      </div>
      
      {/* Filename */}
      <p style={{
        color: '#B8C0E0',
        fontSize: '12px',
        marginBottom: '10px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontWeight: '500'
      }} title={file.original_filename}>
        {file.original_filename}
      </p>
      
      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: '6px',
        justifyContent: 'center'
      }}>
        {/* View Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onView && onView(file);
          }}
          style={{
            flex: 1,
            backgroundColor: 'rgba(0, 194, 255, 0.1)',
            color: '#00C2FF',
            border: '1px solid rgba(0, 194, 255, 0.3)',
            borderRadius: '6px',
            padding: '6px 8px',
            cursor: 'pointer',
            fontSize: '11px',
            fontWeight: '600',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 194, 255, 0.2)';
            e.currentTarget.style.borderColor = '#00C2FF';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 194, 255, 0.1)';
            e.currentTarget.style.borderColor = 'rgba(0, 194, 255, 0.3)';
          }}
          title="Visualiser"
        >
          👁️
        </button>
        
        {/* Download Button */}
        {!isTemp && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDownload && onDownload(file);
            }}
            style={{
              flex: 1,
              backgroundColor: 'rgba(0, 255, 179, 0.1)',
              color: '#00FFB3',
              border: '1px solid rgba(0, 255, 179, 0.3)',
              borderRadius: '6px',
              padding: '6px 8px',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: '600',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 255, 179, 0.2)';
              e.currentTarget.style.borderColor = '#00FFB3';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 255, 179, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(0, 255, 179, 0.3)';
            }}
            title="Télécharger"
          >
            📥
          </button>
        )}
        
        {/* Delete Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onDelete && onDelete(file, e);
          }}
          style={{
            flex: 1,
            backgroundColor: 'rgba(255, 87, 87, 0.1)',
            color: '#FF8A8A',
            border: '1px solid rgba(255, 87, 87, 0.3)',
            borderRadius: '6px',
            padding: '6px 8px',
            cursor: 'pointer',
            fontSize: '11px',
            fontWeight: '600',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 87, 87, 0.2)';
            e.currentTarget.style.borderColor = '#FF5757';
            e.currentTarget.style.color = '#FF5757';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 87, 87, 0.1)';
            e.currentTarget.style.borderColor = 'rgba(255, 87, 87, 0.3)';
            e.currentTarget.style.color = '#FF8A8A';
          }}
          title="Supprimer"
        >
          🗑️
        </button>
      </div>
    </div>
  );
};

// Document Viewer Modal Component
const DocumentViewerModal = ({ file, concessionId, personId, onClose, onDownload, onDelete }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [loadError, setLoadError] = useState(null);
  
  const isImage = ['.jpg', '.jpeg', '.png', '.gif'].includes(file?.file_type?.toLowerCase());
  const isPdf = file?.file_type?.toLowerCase() === '.pdf';
  const isTxt = file?.file_type?.toLowerCase() === '.txt';
  const isGif = file?.file_type?.toLowerCase() === '.gif';
  
  const getFileUrl = () => {
    if (personId) {
      return `${API}/client/concessions/${concessionId}/persons/${personId}/files/${file.id}`;
    }
    return `${API}/client/concessions/${concessionId}/files/${file.id}`;
  };

  // Load file content with authentication
  useEffect(() => {
    if (!file || !concessionId) return;
    
    setIsLoadingContent(true);
    setLoadError(null);
    setBlobUrl(null);
    setTextContent(null);
    
    const loadFile = async () => {
      try {
        const response = await fetch(getFileUrl(), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Erreur ${response.status}: ${response.statusText}`);
        }
        
        if (isTxt) {
          const text = await response.text();
          setTextContent(text);
        } else {
          // For images and PDFs, create a blob URL
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          setBlobUrl(url);
        }
        setIsLoadingContent(false);
      } catch (error) {
        console.error('Error loading file:', error);
        setLoadError(error.message || 'Erreur lors du chargement du fichier');
        setIsLoadingContent(false);
      }
    };
    
    loadFile();
    
    // Cleanup blob URL on unmount
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [file, concessionId, personId]);

  if (!file) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        backdropFilter: 'blur(10px)'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          maxWidth: '95vw',
          maxHeight: '95vh',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with filename and actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          padding: '0 8px'
        }}>
          <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
            <span style={{fontSize: '28px'}}>
              {isGif ? '🎞️' : isImage ? '🖼️' : isPdf ? '📄' : '📝'}
            </span>
            <div>
              <h3 style={{
                color: '#FFFFFF',
                margin: 0,
                fontSize: '18px',
                fontWeight: '600'
              }}>
                {file.original_filename}
              </h3>
              <p style={{
                color: '#5B638C',
                margin: '4px 0 0 0',
                fontSize: '12px'
              }}>
                {isGif ? 'Image animée (GIF)' : isImage ? 'Image' : isPdf ? 'Document PDF' : 'Fichier texte'}
              </p>
            </div>
          </div>
          
          {/* Action buttons */}
          <div style={{display: 'flex', gap: '10px'}}>
            {/* Download */}
            <button
              onClick={() => onDownload && onDownload(file)}
              style={{
                backgroundColor: 'rgba(0, 255, 179, 0.15)',
                color: '#00FFB3',
                border: '1px solid rgba(0, 255, 179, 0.4)',
                borderRadius: '8px',
                padding: '10px 20px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 255, 179, 0.25)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 255, 179, 0.15)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              📥 Télécharger
            </button>
            
            {/* Delete */}
            <button
              onClick={(e) => onDelete && onDelete(file, e)}
              style={{
                backgroundColor: 'rgba(255, 87, 87, 0.15)',
                color: '#FF8A8A',
                border: '1px solid rgba(255, 87, 87, 0.4)',
                borderRadius: '8px',
                padding: '10px 20px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 87, 87, 0.25)';
                e.currentTarget.style.color = '#FF5757';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 87, 87, 0.15)';
                e.currentTarget.style.color = '#FF8A8A';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              🗑️ Supprimer
            </button>
            
            {/* Close */}
            <button
              onClick={onClose}
              style={{
                backgroundColor: 'rgba(138, 146, 178, 0.15)',
                color: '#8A92B2',
                border: '1px solid rgba(138, 146, 178, 0.4)',
                borderRadius: '8px',
                padding: '10px 20px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(138, 146, 178, 0.25)';
                e.currentTarget.style.color = '#FFFFFF';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(138, 146, 178, 0.15)';
                e.currentTarget.style.color = '#8A92B2';
              }}
            >
              ✕ Fermer
            </button>
          </div>
        </div>

        {/* Content area */}
        <div style={{
          backgroundColor: '#1C2133',
          borderRadius: '12px',
          padding: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
          border: '2px solid #3A4359',
          overflow: 'auto',
          maxHeight: 'calc(95vh - 100px)'
        }}>
          {isLoadingContent ? (
            <div style={{textAlign: 'center', padding: '60px', minWidth: '400px'}}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid rgba(0, 194, 255, 0.2)',
                borderTop: '4px solid #00C2FF',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px auto'
              }} />
              <span style={{color: '#8A92B2', fontSize: '16px'}}>Chargement du fichier...</span>
              <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
          ) : loadError ? (
            <div style={{textAlign: 'center', padding: '60px', minWidth: '400px'}}>
              <span style={{fontSize: '60px', marginBottom: '20px', display: 'block'}}>⚠️</span>
              <p style={{color: '#FF8A8A', marginBottom: '10px', fontSize: '16px', fontWeight: '600'}}>
                Erreur de chargement
              </p>
              <p style={{color: '#8A92B2', fontSize: '14px'}}>
                {loadError}
              </p>
            </div>
          ) : isImage && blobUrl ? (
            <img 
              src={blobUrl}
              alt={file.original_filename}
              style={{
                maxWidth: '85vw',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: '8px',
                display: 'block',
                margin: '0 auto'
              }}
            />
          ) : isPdf && blobUrl ? (
            <div style={{width: '85vw', height: '80vh'}}>
              <iframe
                src={`${blobUrl}#toolbar=1&navpanes=0`}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#FFF'
                }}
                title={file.original_filename}
              />
            </div>
          ) : isTxt ? (
            <div style={{
              width: '85vw',
              maxHeight: '80vh',
              overflow: 'auto'
            }}>
              {textContent ? (
                <pre style={{
                  backgroundColor: '#0D1117',
                  padding: '20px',
                  borderRadius: '8px',
                  color: '#B8C0E0',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  margin: 0,
                  fontFamily: 'Monaco, Consolas, monospace'
                }}>
                  {textContent}
                </pre>
              ) : (
                <div style={{textAlign: 'center', padding: '40px'}}>
                  <span style={{fontSize: '60px', marginBottom: '20px', display: 'block'}}>📝</span>
                  <p style={{color: '#8A92B2', marginBottom: '20px'}}>
                    Aperçu non disponible pour ce fichier
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div style={{textAlign: 'center', padding: '60px'}}>
              <span style={{fontSize: '80px', marginBottom: '20px', display: 'block'}}>📁</span>
              <p style={{color: '#8A92B2', marginBottom: '20px'}}>
                Aperçu non disponible pour ce type de fichier
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { DocumentCard, DocumentViewerModal };
export default DocumentCard;
